import React, { useEffect, useState } from 'react';
import { X, History, MapPin, ArrowRight, User, Calendar, FileText } from 'lucide-react';
import api from './axiosConfig';

const MoveHistoryModal = ({ isOpen, onClose, assetNum }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && assetNum) {
            const fetchHistory = async () => {
                setLoading(true);
                try {
                    const res = await api.get(`/api/assets/move/history/${assetNum}`);
                    setHistory(res.data);
                } catch (err) {
                    console.error("Failed to fetch move history");
                } finally {
                    setLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, assetNum]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[10006] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-5xl shadow-2xl rounded-sm border border-gray-300 flex flex-col max-h-[85vh]">
                
                {/* Header - Maximo Style */}
                <div className="bg-[#1e293b] text-white p-3 flex justify-between items-center border-b-2 border-blue-500">
                    <div className="flex items-center gap-3">
                        <History size={20} className="text-blue-400" />
                        <div>
                            <h3 className="font-black text-xs uppercase tracking-widest">Asset Move/Modify History</h3>
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Audit Trail for Asset: {assetNum}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded transition-colors"><X size={20}/></button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                    {loading ? (
                        <div className="flex justify-center p-20 font-black text-gray-300 animate-pulse uppercase tracking-widest italic">Fetching Audit Logs...</div>
                    ) : history.length === 0 ? (
                        <div className="text-center p-20 text-gray-400 italic font-bold">No movement records found for this asset.</div>
                    ) : (
                        <div className="overflow-x-auto border bg-white shadow-md rounded-sm">
                            <table className="w-full text-[11px] border-collapse text-left">
                                <thead className="bg-[#f8fafc] border-b-2 border-gray-200 text-[#005495] font-black uppercase">
                                    <tr>
                                        <th className="p-3 border-r">Date</th>
                                        <th className="p-3 border-r">From Location / Parent</th>
                                        <th className="p-3 border-r text-center w-10"></th>
                                        <th className="p-3 border-r">To Location / Parent</th>
                                        <th className="p-3 border-r">Reason / Memo</th>
                                        <th className="p-3">Moved By</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 font-bold">
                                    {history.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-blue-50/50 transition-colors group">
                                            <td className="p-3 border-r text-gray-600 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={12} className="text-gray-400" />
                                                    {new Date(item.movedate).toLocaleString('sv-SE').slice(0, 16)}
                                                </div>
                                            </td>
                                            
                                            <td className="p-3 border-r bg-gray-50/50">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-gray-500 line-through decoration-red-300">{item.from_location || 'EMPTY'}</span>
                                                    <span className="text-[9px] text-gray-400 italic font-normal">Parent: {item.from_parent || 'None'}</span>
                                                </div>
                                            </td>

                                            <td className="p-3 border-r text-center">
                                                <ArrowRight size={14} className="text-blue-500 mx-auto group-hover:translate-x-1 transition-transform" />
                                            </td>

                                            <td className="p-3 border-r">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-blue-700 font-black">{item.to_location || 'EMPTY'}</span>
                                                    <span className="text-[9px] text-blue-400 italic font-black uppercase">Parent: {item.to_parent || 'None'}</span>
                                                </div>
                                            </td>

                                            <td className="p-3 border-r text-gray-600 font-normal italic max-w-xs truncate">
                                                <div className="flex items-start gap-2">
                                                    <FileText size={12} className="text-gray-300 mt-0.5" />
                                                    {item.memo}
                                                </div>
                                            </td>

                                            <td className="p-3 text-gray-700 text-[10px] uppercase">
                                                <div className="flex items-center gap-2">
                                                    <User size={12} className="text-gray-400" />
                                                    {item.moveby}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-gray-100 p-4 border-t flex justify-end">
                    <button 
                        onClick={onClose}
                        className="px-8 py-2 bg-slate-800 text-white font-black text-[10px] uppercase shadow-lg hover:bg-black transition-all"
                    >
                        Close History
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MoveHistoryModal;